import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, ArrowLeft, DatabaseBackup, Download, Info, Archive, ExternalLink, Sparkles, History, ChevronDown } from 'lucide-react';
import { fetchFormAssemblyFormDetails, fetchFormBackups, backupFormAssemblyForm, summarizeFormWithClaude, fetchFormSummary, restoreFormFromBackup, fetchFormAssemblyForms } from '@/services/formassemblyService';
import { FormDetailData, FormBackup, FormSummary, FormAssemblyForm } from '@/types/formassembly';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { FormSettingsEditor } from '@/components/FormSettingsEditor';
import { logger } from '@/lib/logger';

const getBackupFormName = (backup: FormBackup): string => {
  const formData = backup.form_data_json as {
    form?: { name?: string };
    name?: string;
  };

  return formData.form?.name || formData.name || 'N/A';
};

const FormDetailPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatDateSafe } = useTimezone();
  const { user } = useAuth();
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const [currentSummaryText, setCurrentSummaryText] = useState<string | null>(null);
  const aiSummariesEnabled =
    import.meta.env.VITE_ENABLE_FORMASSEMBLY_AI_SUMMARIES === 'true';

  // DEBUG: Log feature flag value
  logger.debug('[FormDetailPage] VITE_ENABLE_EXTENDED_BACKUP_SETTINGS:', import.meta.env.VITE_ENABLE_EXTENDED_BACKUP_SETTINGS, 'Type:', typeof import.meta.env.VITE_ENABLE_EXTENDED_BACKUP_SETTINGS);

  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] = useState<FormBackup | null>(null);
  const [typedConfirmName, setTypedConfirmName] = useState("");

  // CSS prettifier function
  const prettifyCSS = (css: string): string => {
    return css
      // Add line breaks after opening braces
      .replace(/\{/g, ' {\n  ')
      // Add line breaks after closing braces
      .replace(/\}/g, '\n}\n')
      // Add line breaks after semicolons
      .replace(/;/g, ';\n  ')
      // Clean up multiple line breaks
      .replace(/\n\s*\n/g, '\n')
      // Clean up extra spaces before closing braces
      .replace(/\n\s*\}/g, '\n}')
      // Add proper spacing around commas in selectors
      .replace(/,/g, ',\n')
      // Clean up any trailing whitespace
      .split('\n').map(line => line.trim()).join('\n')
      // Remove empty lines
      .replace(/\n\n+/g, '\n');
  };

  const parsedFormId = parseInt(formId || '0', 10);

  const { data: formDetails, isLoading: isLoadingDetails, error: detailsError, refetch: refetchDetails } = useQuery<FormDetailData, Error>({
    queryKey: ['formAssemblyFormDetails', parsedFormId],
    queryFn: () => fetchFormAssemblyFormDetails(parsedFormId),
    enabled: !!parsedFormId,
  });

  // Fetch the forms list to get the same data source as the dashboard
  const { data: forms } = useQuery<FormAssemblyForm[], Error>({
    queryKey: ['formAssemblyForms'],
    queryFn: fetchFormAssemblyForms,
    enabled: !!parsedFormId,
  });

  // DEBUG: Log forms data to understand the issue
  useEffect(() => {
    if (forms) {
      logger.debug('[FormDetailPage] Forms loaded:', forms.length, 'forms');
      logger.debug('[FormDetailPage] Looking for formId (number):', parsedFormId, 'type:', typeof parsedFormId);
      logger.debug('[FormDetailPage] Form IDs in list (with types):', forms.map(f => ({ id: f.id, type: typeof f.id })));
      const matchingForm = forms.find(f => f.id.toString() === parsedFormId.toString());
      logger.debug('[FormDetailPage] Matching form (after string conversion):', matchingForm);
    } else {
      logger.debug('[FormDetailPage] Forms array is undefined/null');
    }
  }, [forms, parsedFormId]);

  // Get the current form name from the forms list (source of truth from dashboard)
  const currentFormName = useMemo(() => {
    const matchingForm = forms?.find(f => f.id.toString() === parsedFormId.toString());
    return matchingForm?.name || formDetails?.form?.name || 'Unknown Form';
  }, [forms, parsedFormId, formDetails?.form?.name]);

  // Show toast when form details fail to load
  useEffect(() => {
    if (detailsError) {
      const subject = encodeURIComponent(`Form Load Error - Form ID ${formId}`);
      const body = encodeURIComponent(`I encountered an error while loading a form:\n\nForm ID: ${formId}\nError: ${detailsError.message}\n\nPlease help me resolve this issue.`);
      const mailtoLink = `mailto:support@fab.day?subject=${subject}&body=${body}`;

      toast({
        variant: "destructive",
        title: "Error Loading Form Details",
        description: (
          <div className="space-y-2">
            <p>{detailsError.message || `Could not load form ${formId}.`}</p>
            <p className="text-sm">
              Need help?{" "}
              <a
                href={mailtoLink}
                className="underline hover:text-white"
                onClick={(e) => e.stopPropagation()}
              >
                Contact support@fab.day
              </a>
            </p>
          </div>
        ),
      });
    }
  }, [detailsError, formId]);

  const { data: backups, isLoading: isLoadingBackups, error: backupsError, refetch: refetchBackups } = useQuery<FormBackup[], Error>({
    queryKey: ['formBackups', formId],
    queryFn: () => fetchFormBackups(formId!),
    enabled: !!formId,
  });

  const { data: storedSummary, isLoading: isLoadingStoredSummary, error: storedSummaryError, refetch: refetchStoredSummary } = useQuery<FormSummary | null, Error>({
    queryKey: ['formSummary', formId],
    queryFn: () => formId ? fetchFormSummary(formId) : Promise.resolve(null),
    enabled: aiSummariesEnabled && !!formId,
  });

  const generateSummaryMutation = useMutation<
    { summary: string | null; rateLimitHit?: boolean }, 
    Error & { rateLimitHit?: boolean; status?: number },
    { formJson: Record<string, unknown>; formIdExternal: string }
  >({
    mutationFn: ({ formJson, formIdExternal }) => summarizeFormWithClaude({ formJson, formIdExternal }),
    onSuccess: (data) => {
      if (data.summary) {
        setCurrentSummaryText(data.summary);
        toast({
          title: "Summary Generated",
          description: "AI-powered form summary has been successfully generated and saved.",
        });
      } else if (data.rateLimitHit) {
         toast({
          variant: "default",
          title: "Summary Generation Limit",
          description: "You've reached the limit for generating summaries for this form this month.",
        });
      }
      if (formId) {
        queryClient.invalidateQueries({ queryKey: ['formSummary', formId] });
      }
    },
    onError: (error) => {
      let description = error.message || "An unknown error occurred trying to generate the summary.";
      if (error.rateLimitHit || error.status === 429) {
        description = "You've reached the limit for generating summaries for this form this month. Please try again next month.";
      } else if (error.status === 529) {
        description = "The AI service is currently experiencing high demand. Please try again in a few moments.";
      } else if (error.message === "REAUTH_REQUIRED") {
        description = "Re-authentication required for FormAssembly.";
      }
      
      toast({
        variant: "destructive",
        title: "Summary Generation Failed",
        description: description,
      });
    },
  });

  const backupFormMutation = useMutation<
    { success: boolean; backupId: string; message: string },
    Error,
    { formId: number; formName: string }
  >({
    mutationFn: ({ formId }) => backupFormAssemblyForm(formId),
    onSuccess: (_, variables) => {
      toast({
        title: "Backup Successful",
        description: `Form '${variables.formName}' (ID: ${variables.formId}) backed up successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['formBackups', formId?.toString()] });
      queryClient.invalidateQueries({ queryKey: ['allUserFormBackups'] });
    },
    onError: (error, variables) => {
      if (error.message === "REAUTH_REQUIRED") {
        toast({
          variant: "destructive",
          title: "Backup Failed",
          description: "Re-authentication required. Please disconnect and connect FormAssembly again."
        });
        return;
      }

      // Extract form ID from error message if present
      const formIdMatch = error.message.match(/form (\d+)/i);
      const formId = formIdMatch ? formIdMatch[1] : variables;

      // Create mailto link with pre-populated subject and body
      const subject = encodeURIComponent(`Form Backup Error - Form ID ${formId}`);
      const body = encodeURIComponent(`I encountered an error while backing up a form:

Form ID: ${formId}
Error: ${error.message}

Please help me resolve this issue.`);
      const mailtoLink = `mailto:support@fab.day?subject=${subject}&body=${body}`;

      toast({
        variant: "destructive",
        title: "Backup Failed",
        description: (
          <div className="space-y-2">
            <p>{error.message || `Could not backup form ${formId}.`}</p>
            <p className="text-sm">
              Need help?{" "}
              <a
                href={mailtoLink}
                className="underline hover:text-white"
                onClick={(e) => e.stopPropagation()}
              >
                Contact support@fab.day
              </a>
            </p>
          </div>
        ),
      });
    },
  });

  const restoreFormMutation = useMutation<
    FormAssemblyForm,
    Error,
    { backup: FormBackup; formName: string; originalFormName: string }
  >({
    mutationFn: ({ backup, formName }) => restoreFormFromBackup(backup, formName),
    onSuccess: (createdForm, variables) => {
      const displayName = variables.originalFormName || 'Form';
      toast({
        title: "Restore Successful",
        description: `Form '${displayName}' (ID: ${createdForm.id}) created successfully in FormAssembly from backup.`,
      });
      setIsRestoreConfirmOpen(false);
      setSelectedBackupForRestore(null);
      setTypedConfirmName("");
      queryClient.invalidateQueries({ queryKey: ['formAssemblyForms'] });
    },
    onError: (error) => {
      let description = error.message;
      if (error.message === "REAUTH_REQUIRED") {
        description = "Re-authentication required. Please disconnect and connect FormAssembly again, then try restoring.";
      }
      toast({
        variant: "destructive",
        title: "Restore Failed",
        description: description || "Could not restore form from backup.",
      });
    },
  });

  useEffect(() => {
    if (
      aiSummariesEnabled &&
      storedSummary?.summary_text &&
      !generateSummaryMutation.isPending &&
      !generateSummaryMutation.data
    ) {
      setCurrentSummaryText(storedSummary.summary_text);
    }
  }, [aiSummariesEnabled, storedSummary, generateSummaryMutation.isPending, generateSummaryMutation.data]);
  
  // USE THE FOLLOWING IF ANY ACCORDIAN'S ARE OPEN BY DEFAULT
  // useEffect(() => {
  //   if (formDetails?.rawJson) {
  //     setOpenAccordionItems(prevItems => {
  //       if (!prevItems.includes("form-summary")) {
  //         return [...prevItems, "form-summary"];
  //       }
  //       return prevItems;
  //     });
  //   }
  // }, [formDetails?.rawJson]);

  const handleGenerateSummary = () => {
    if (!aiSummariesEnabled) {
      toast({
        variant: "destructive",
        title: "AI Summaries Disabled",
        description: "FormAssembly AI summaries are currently disabled.",
      });
      return;
    }

    if (formDetails?.rawJson && formId) {
      setCurrentSummaryText(null);
      generateSummaryMutation.mutate({
        formJson: formDetails.rawJson,
        formIdExternal: formId
      });
    } else {
      toast({ variant: "destructive", title: "Error", description: "Form JSON data or Form ID is not available to generate summary." });
    }
  };

  const handleBackupForm = () => {
    if (parsedFormId && formDetails?.form) {
      backupFormMutation.mutate({ formId: parsedFormId, formName: currentFormName });
    } else {
      toast({ variant: "destructive", title: "Error", description: "Form ID or form details are invalid, cannot backup." });
    }
  };

  const handleDownloadBackup = (backup: FormBackup) => {
    const jsonBlob = new Blob([JSON.stringify(backup.form_data_json, null, 2)], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const jsonLink = document.createElement('a');
    jsonLink.href = jsonUrl;
    jsonLink.download = `form_${backup.form_id_external}_backup_${backup.id}_${new Date(backup.created_at).toISOString().split('T')[0]}.json`;
    document.body.appendChild(jsonLink);
    jsonLink.click();
    document.body.removeChild(jsonLink);
    URL.revokeObjectURL(jsonUrl);

    const xmlBlob = new Blob([backup.form_data_xml], { type: 'application/xml' });
    const xmlUrl = URL.createObjectURL(xmlBlob);
    const xmlLink = document.createElement('a');
    xmlLink.href = xmlUrl;
    xmlLink.download = `form_${backup.form_id_external}_backup_${backup.id}_${new Date(backup.created_at).toISOString().split('T')[0]}.xml`;
    document.body.appendChild(xmlLink);
    xmlLink.click();
    document.body.removeChild(xmlLink);
    URL.revokeObjectURL(xmlUrl);
  };

  const handleOpenRestoreDialog = (backup: FormBackup) => {
    if (!formDetails?.form) return;
    setSelectedBackupForRestore(backup);
    setTypedConfirmName('');
    setIsRestoreConfirmOpen(true);
  };

  const handleConfirmRestore = () => {
    if (!selectedBackupForRestore || !formDetails?.form) return;

    const backupFormData = selectedBackupForRestore.form_data_json?.form as { name?: string } | undefined;
    const originalFormName = backupFormData?.name || currentFormName;

    restoreFormMutation.mutate({
      backup: selectedBackupForRestore,
      formName: `FAB RESTORE - ${currentFormName}`,
      originalFormName: originalFormName,
    });
  };

  const displaySummaryText = generateSummaryMutation.data?.summary || currentSummaryText;
  const hasAnySummary = !!displaySummaryText;
  const isGenerating = generateSummaryMutation.isPending;
  const generationError = generateSummaryMutation.error;

  if (!formId || parsedFormId === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-grow container mx-auto py-8">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>Invalid Form ID provided.</AlertDescription>
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow container mx-auto py-8 px-4">
        <Button variant="outline" onClick={() => navigate('/')} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Forms List
        </Button>

        {isLoadingDetails && (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="ml-2 text-muted-foreground">Loading form details...</p>
          </div>
        )}

        {detailsError && (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Error Loading Form Details</AlertTitle>
            <AlertDescription>
              {detailsError.message}
              {detailsError.message === "REAUTH_REQUIRED" ? (
                <Button variant="link" onClick={() => navigate('/formassembly?reauth=true')} className="ml-2">Reconnect FormAssembly</Button>
              ) : (
                <Button variant="link" onClick={() => refetchDetails()} className="ml-2">Try again</Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {formDetails && formDetails.form && (
          <>
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{currentFormName}</CardTitle>
                <CardDescription>ID: {parsedFormId} - Last Updated: {
                  (() => {
                    const matchingForm = forms?.find(f => f.id.toString() === parsedFormId.toString());
                    if (matchingForm?.modified) {
                      return formatDateSafe(matchingForm.modified);
                    }
                    return formatDateSafe(formDetails.form.last_updated_at);
                  })()
                }</CardDescription>
              </CardHeader>
              <CardContent>
                <h3 className="text-lg font-semibold mb-2">Key Details:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Status: {formDetails.form.archived ? 'Archived' : 'Active'}</li>
                  <li>Created: {
                    (() => {
                      // Find the matching form from the forms list (same data source as dashboard)
                      const matchingForm = forms?.find(f => f.id.toString() === parsedFormId.toString());
                      if (matchingForm?.modified) {
                        return formatDateSafe(matchingForm.modified);
                      }
                      // Fallback to the detail form data
                      return formatDateSafe(formDetails.form.last_updated_at);
                    })()
                  }</li>
                  {formDetails.form.theme_id && <li>Theme ID: {formDetails.form.theme_id}</li>}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2">
                  {parsedFormId ? (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="text-primary border-primary hover:text-primary hover:bg-primary/10 cursor-pointer"
                    >
                      <a href={`https://app.formassembly.com/forms/edit/summary/${parsedFormId}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-1" /> View on FormAssembly
                      </a>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-primary border-primary"
                      disabled={true}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" /> View on FormAssembly (Link unavailable)
                    </Button>
                  )}
                  <Button 
                    size="sm"
                    onClick={handleBackupForm}
                    disabled={backupFormMutation.isPending}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    {backupFormMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Archive className="h-4 w-4 mr-1" />
                    )}
                    Backup Form
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DatabaseBackup className="mr-2 h-5 w-5" /> Form Backups
                </CardTitle>
                <CardDescription>List of all backups created for this form. Restore creates a NEW form in FormAssembly.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingBackups && (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="ml-2 text-muted-foreground">Loading backups...</p>
                  </div>
                )}
                {backupsError && (
                  <Alert variant="destructive">
                    <AlertTitle>Error Loading Backups</AlertTitle>
                    <AlertDescription>
                      {backupsError.message}
                      <Button variant="link" onClick={() => refetchBackups()} className="ml-2">Try again</Button>
                    </AlertDescription>
                  </Alert>
                )}
                {!isLoadingBackups && !backupsError && (!backups || backups.length === 0) && (
                  <p className="text-muted-foreground">No backups found for this form.</p>
                )}
                {backups && backups.length > 0 && (
                  <TooltipProvider>
                    <div className="overflow-x-auto">
                      <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Form Name (at backup)</TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            Created At
                            <ChevronDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                        <TableHead>Backup ID</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backups.map((backup) => {
                        const backupFormName = getBackupFormName(backup);
                        return (
                          <TableRow key={backup.id}>
                            <TableCell className="text-xs">
                              {backupFormName.length > 50 ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">{backupFormName.substring(0, 50)}...</span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{backupFormName}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                backupFormName
                              )}
                            </TableCell>
                            <TableCell>{formatDateSafe(backup.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs">{backup.id}</TableCell>
                          <TableCell className="space-x-2 flex items-center">
                            <Button variant="outline" size="sm" onClick={() => handleDownloadBackup(backup)}>
                              <Download className="mr-1 h-4 w-4" /> Download
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-red-500 bg-white text-red-500 hover:bg-red-50 hover:text-red-600 focus:ring-red-500"
                              onClick={() => handleOpenRestoreDialog(backup)}
                              disabled={restoreFormMutation.isPending && selectedBackupForRestore?.id === backup.id}
                            >
                              {restoreFormMutation.isPending && selectedBackupForRestore?.id === backup.id ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                              ) : (
                                <History className="mr-1 h-4 w-4" />
                              )}
                              Restore
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                      </Table>
                    </div>
                  </TooltipProvider>
                )}
              </CardContent>
            </Card>

            {/* Extended Form Settings - Feature Flagged */}
            {import.meta.env.VITE_ENABLE_EXTENDED_BACKUP_SETTINGS === 'true' && (
              <FormSettingsEditor formIdExternal={formId!} />
            )}

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Info className="mr-2 h-5 w-5" /> Form Details
                </CardTitle>
                <CardDescription>
                  {aiSummariesEnabled
                    ? 'Raw JSON, XML, AI-powered summary, theme, and connector definitions for this form.'
                    : 'Raw JSON, XML, theme, and connector definitions for this form.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion 
                    type="multiple" 
                    className="w-full"
                    value={openAccordionItems}
                    onValueChange={setOpenAccordionItems}
                >
                  {aiSummariesEnabled && (
                    <AccordionItem value="form-summary">
                      <AccordionTrigger>
                          <div className="flex items-center">
                              <Sparkles className="mr-2 h-4 w-4 text-yellow-500" /> Form Summary (AI-Powered)
                          </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {isLoadingStoredSummary && !displaySummaryText && (
                           <div className="flex items-center text-muted-foreground">
                              <Loader2 className="h-5 w-5 animate-spin mr-2" />
                              <span>Loading stored summary...</span>
                            </div>
                        )}
                        {!isLoadingStoredSummary && formDetails?.rawJson ? (
                          <>
                            <Button
                              onClick={handleGenerateSummary}
                              disabled={isGenerating || !formDetails.rawJson || !parsedFormId}
                              size="sm"
                              className="mb-4 bg-blue-500 hover:bg-blue-600 text-white"
                            >
                              {isGenerating ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Sparkles className="h-4 w-4 mr-2" />
                              )}
                              {hasAnySummary ? 'Regenerate Summary' : 'Generate Summary'}
                            </Button>
                            {isGenerating && (
                              <div className="flex items-center text-muted-foreground">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                <span>Generating summary with AI... this may take a moment.</span>
                              </div>
                            )}
                            {generationError && (
                              <Alert variant="destructive" className="my-2">
                                <AlertTitle>Error Generating Summary</AlertTitle>
                                <AlertDescription>{generationError.message}</AlertDescription>
                              </Alert>
                            )}
                             {storedSummaryError && !isGenerating && !generationError &&(
                               <Alert variant="destructive" className="my-2">
                                 <AlertTitle>Error Loading Stored Summary</AlertTitle>
                                 <AlertDescription>{storedSummaryError.message}</AlertDescription>
                               </Alert>
                             )}
                            {displaySummaryText && !isGenerating && !generationError && (
                              <div className="prose prose-sm dark:prose-invert max-w-none bg-muted p-4 rounded-md whitespace-pre-line text-foreground">
                                {displaySummaryText}
                              </div>
                            )}
                            {!displaySummaryText && !isGenerating && !generationError && !isLoadingStoredSummary && !storedSummaryError && (
                               <p className="text-muted-foreground">Click the button above to generate an AI-powered summary of the form's structure and layout. Summaries are saved and can be regenerated once per month per form.</p>
                            )}
                          </>
                        ) : (
                          !isLoadingStoredSummary && <p className="text-muted-foreground p-4">Form JSON data is not available to generate a summary.</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {formDetails.rawJson && (
                    <AccordionItem value="form-json">
                      <AccordionTrigger>Form Data (JSON)</AccordionTrigger>
                      <AccordionContent>
                        <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96">
                          {JSON.stringify(formDetails.rawJson, null, 2)}
                        </pre>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {formDetails.rawXml && (
                    <AccordionItem value="form-xml">
                      <AccordionTrigger>Form Data (XML)</AccordionTrigger>
                      <AccordionContent>
                        <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
                          {formDetails.rawXml}
                        </pre>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {formDetails.connectors && formDetails.connectors.length > 0 && (
                    <AccordionItem value="connector-definitions">
                      <AccordionTrigger>Connector Definitions ({formDetails.connectors.length})</AccordionTrigger>
                      <AccordionContent>
                        {formDetails.connectors.map((connDef) => (
                          <Accordion type="single" collapsible className="w-full mt-2" key={connDef.id}>
                            <AccordionItem value={`connector-${connDef.id}`}>
                              <AccordionTrigger className="text-sm">
                                Connector ID: {connDef.id} {connDef.error ? <span className="text-destructive ml-2">(Error loading definition)</span> : ''}
                              </AccordionTrigger>
                              <AccordionContent>
                                {connDef.error && <p className="text-destructive mb-2">Error: {connDef.error}</p>}
                                {connDef.definition ? (
                                  <pre className="bg-muted-foreground/10 p-3 rounded-md text-xs overflow-x-auto max-h-80">
                                    {JSON.stringify(connDef.definition, null, 2)}
                                  </pre>
                                ) : !connDef.error && (
                                  <p className="text-muted-foreground p-2">No definition data for this connector.</p>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                   {formDetails.connectors && formDetails.connectors.length === 0 && (
                     <AccordionItem value="connector-definitions-empty" disabled>
                        <AccordionTrigger>Connector Definitions (0)</AccordionTrigger>
                        <AccordionContent>
                            <p className="text-muted-foreground p-4">No connector definitions found for this form.</p>
                        </AccordionContent>
                    </AccordionItem>
                   )}
                  
                   
                  {/* Backed up theme data from latest backup */}
                  {backups && backups.length > 0 && backups[0].backup_themes && backups[0].backup_themes.length > 0 && (
                    <AccordionItem value="backup-themes">
                      <AccordionTrigger>Backed Up Themes ({backups[0].backup_themes.length})</AccordionTrigger>
                      <AccordionContent>
                        {backups[0].backup_themes.map((backupTheme) => (
                          <Accordion type="single" collapsible className="w-full mt-2" key={backupTheme.id}>
                            <AccordionItem value={`backup-theme-${backupTheme.id}`}>
                              <AccordionTrigger className="text-sm">
                                {backupTheme.name} (ID: {backupTheme.theme_id_external})
                              </AccordionTrigger>
                              <AccordionContent>
                                                                 {backupTheme.css_data ? (
                                   <div className="space-y-4">
                                     <div>
                                       <h4 className="font-semibold mb-2">CSS Data ({backupTheme.css_data.length} characters):</h4>
                                       <div className="bg-slate-50 border rounded-md overflow-hidden">
                                         <div className="bg-slate-100 px-3 py-2 border-b text-xs font-medium text-slate-600">
                                           CSS Theme Styles
                                         </div>
                                         <pre className="p-4 text-xs overflow-x-auto max-h-96 whitespace-pre leading-relaxed text-slate-800 font-mono">
                                           {prettifyCSS(backupTheme.css_data)}
                                         </pre>
                                       </div>
                                     </div>
                                    {backupTheme.theme_metadata_json && (
                                      <div>
                                        <h4 className="font-semibold mb-2">Theme Metadata:</h4>
                                        <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto max-h-40">
                                          {JSON.stringify(backupTheme.theme_metadata_json, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground p-2">No CSS data available for this theme.</p>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                </Accordion>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {selectedBackupForRestore && (
        <AlertDialog open={isRestoreConfirmOpen} onOpenChange={setIsRestoreConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Restore from Backup</AlertDialogTitle>
              {formDetails && formDetails.form && (
                <AlertDialogDescription>
                  This will create a <strong>NEW</strong> form in your form provider using the backup
                  created on{' '}
                  {formatDateSafe(selectedBackupForRestore.created_at)}.
                  <br />
                  The new form will be named "FAB RESTORE - {currentFormName}".
                  <br />
                  To confirm, please type the original form name, "{currentFormName}", in the box below.
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <div className="py-4">
              <label htmlFor="confirm-restore-input" className="sr-only">Type the original form title to confirm</label>
              <Input
                id="confirm-restore-input"
                value={typedConfirmName}
                onChange={(e) => setTypedConfirmName(e.target.value)}
                placeholder="Type original form name to confirm"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmRestore}
                disabled={typedConfirmName !== currentFormName || restoreFormMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {restoreFormMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default FormDetailPage;
